import React, { Component } from "react";
import { Card, Button, Spinner, Form } from 'react-bootstrap';
import { FaSyncAlt } from "react-icons/fa";
import api from "../../../services/api";
import { formataDataExibicao } from '../../../common/utils';
import moment from "moment-timezone";

const LIMITE_COLUNA = 10;
const LARGURA_COLUNA = 272;
const LARGURA_COLUNA_RECOLHIDA = 46;

const COR_NEUTRA = '#c9d2de';

// O status do programa tem cor configurável, mas boa parte dos programas nunca
// alterou o padrão (#000000) ou deixou em branco — nesses casos o card usaria uma
// borda preta, que parece defeito. Cai no neutro.
const corDoStatus = (status) => {
    const cor = (status?.cor || '').trim();
    if (!cor || cor.toLowerCase() === '#000000' || cor.toLowerCase() === '#000') return COR_NEUTRA;
    return cor;
};

/*
 * TEMPO NA ETAPA — desligado por enquanto (mantido para retomar depois).
 *
 * A ideia era colorir o card pelo tempo parado na etapa, que é o melhor sinal de
 * "este paciente travou". O problema é que o limite do que é "muito tempo" varia
 * por programa E por etapa: há etapa em que 1 mês é normal e outra em que 3 dias
 * já é atraso. Um corte fixo (4/10/20 dias) marcaria quase tudo como crítico em
 * uns programas e nada em outros.
 *
 * Para ligar isso de verdade falta uma configuração de prazo esperado por etapa
 * (ex.: programas_etapas.prazo_dias), e aí as faixas passam a ser relativas a ele
 * — algo como <50%, 50-100%, 100-150%, >150% do prazo.
 *
 * const FAIXAS = [
 *     { min: 20, fg: '#a41c12', bg: '#fdecea', accent: '#c0362a', label: '20+ dias' },
 *     { min: 10, fg: '#9a4d05', bg: '#fdf0e2', accent: '#d2711a', label: '10–19 dias' },
 *     { min: 4,  fg: '#7a5c00', bg: '#fbf6df', accent: '#c9a227', label: '4–9 dias' },
 *     { min: 0,  fg: '#4b5768', bg: '#f1f4f8', accent: '#c9d2de', label: 'até 3 dias' },
 * ];
 * const faixaDe = (dias) => FAIXAS.find(f => (dias ?? 0) >= f.min) || FAIXAS[FAIXAS.length - 1];
 */

// Visão em kanban dos atendimentos: 1 coluna = 1 etapa do programa. Somente leitura
// (clicar no card abre o atendimento) — não há arrastar entre colunas, porque mudar
// de etapa dispara o fluxo de registro de etapa e não deve acontecer sem intenção.
//
// Cada coluna pagina sozinha: a API filtra por etapa_id (já excluindo cancelados e
// finalizados) e devolve o count, então dá para mostrar "10 de 70" e buscar mais só
// da coluna clicada — carregar o programa inteiro de uma vez não escala.
class KanbanAtendimentos extends Component {
    constructor(props) {
        super(props);
        this.state = {
            etapas: [],
            colunas: {},        // etapa_id -> { itens, total, pagina, carregando }
            recolhidas: {},     // etapa_id -> bool
            recolherVazias: true,
            carregandoEtapas: true,
            atualizando: false,
            atualizadoEm: null,
            erro: '',
        };
    }

    componentDidMount() {
        this.carregarEtapas();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.programaId !== this.props.programaId) {
            this.setState({ etapas: [], colunas: {}, carregandoEtapas: true }, () => this.carregarEtapas());
            return;
        }

        // As abas e os filtros do topo valem para o kanban: quando mudam, cada coluna
        // é recarregada da primeira página (compara o conteúdo porque o objeto de
        // filtros é remontado a cada render da listagem).
        if (JSON.stringify(prevProps.filtrosBase) !== JSON.stringify(this.props.filtrosBase)) {
            this.setState({ colunas: {} }, () => {
                this.todasAsColunas().forEach(coluna => this.carregarColuna(coluna.id, 1));
            });
        }
    }

    // Colunas que não são etapas: o que ficou fora do fluxo e os desfechos.
    // "Sem etapa" só é exibida se houver alguém nela; as outras vão sempre no fim.
    colunasEspeciais = () => ([
        { id: 'sem_etapa', nome: 'Sem etapa', params: { sem_etapa: 1 }, ocultarSeVazia: true },
        { id: 'finalizados', nome: 'Finalizados', params: { situacao_atendimento: 'finalizados' } },
        { id: 'cancelados', nome: 'Cancelados', params: { situacao_atendimento: 'cancelados' } },
    ]);

    // A aba do topo manda: se ela pede só "Andamento", as colunas de desfecho somem
    // em vez de contradizer o filtro; se pede "Finalizados", só aquela faz sentido.
    colunaEspecialCabe = (especial) => {
        const situacao = this.props.filtrosBase?.situacao_atendimento;
        if (!situacao || situacao === 'todos') return true;

        if (especial.id === 'finalizados') return situacao === 'finalizados';
        if (especial.id === 'cancelados') return situacao === 'cancelados';
        return !['finalizados', 'cancelados'].includes(situacao);   // sem etapa é de em-andamento
    };

    todasAsColunas = () => [
        ...this.state.etapas,
        ...this.colunasEspeciais().filter(this.colunaEspecialCabe),
    ];

    carregarEtapas = async () => {
        const { programaId } = this.props;
        if (!programaId) return;

        try {
            const { data } = await api.get(`/programas/etapas/?programa_id=${programaId}&status=1&limit=100`);
            const etapas = (data.results || data || []).slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0));

            this.setState({ etapas, carregandoEtapas: false }, () => {
                this.todasAsColunas().forEach(coluna => this.carregarColuna(coluna.id, 1));
            });
        } catch (e) {
            console.error('Erro ao carregar etapas do programa:', e);
            this.setState({ carregandoEtapas: false, erro: 'Não foi possível carregar as etapas do programa.' });
        }
    };

    // recarregar=true refaz a coluna do zero trazendo de uma vez tudo que ela já tinha
    // aberto, para o "ver mais" não ser desfeito a cada atualização.
    carregarColuna = async (etapaId, pagina, recarregar = false) => {
        const { programaId, filtrosBase = {} } = this.props;
        const especial = this.colunasEspeciais().find(c => c.id === etapaId);

        this.setState(prev => ({
            colunas: {
                ...prev.colunas,
                [etapaId]: { ...(prev.colunas[etapaId] || { itens: [], total: 0 }), carregando: true },
            },
        }));

        try {
            const params = {
                ...filtrosBase,
                programa_id: programaId,
                page: recarregar ? 1 : pagina,
                limit: recarregar ? LIMITE_COLUNA * pagina : LIMITE_COLUNA,
            };

            if (especial) {
                // desfecho tem recorte próprio: sobrepõe a situação vinda das abas
                Object.assign(params, especial.params);
            } else {
                params.etapa_id = etapaId;
            }

            const { data } = await api.get('/atendimentos/', { params });
            const novos = data.results || [];

            this.setState(prev => {
                const atual = prev.colunas[etapaId] || { itens: [] };
                return {
                    colunas: {
                        ...prev.colunas,
                        [etapaId]: {
                            itens: (pagina === 1 || recarregar) ? novos : [...atual.itens, ...novos],
                            total: data.count ?? novos.length,
                            pagina,
                            carregando: false,
                        },
                    },
                };
            });
        } catch (e) {
            console.error(`Erro ao carregar atendimentos da etapa ${etapaId}:`, e);
            this.setState(prev => ({
                colunas: {
                    ...prev.colunas,
                    [etapaId]: { ...(prev.colunas[etapaId] || { itens: [], total: 0 }), carregando: false },
                },
            }));
        }
    };

    // O kanban é uma foto do momento em que carregou: quem registra a etapa em outra
    // aba precisa de um jeito de trazer o card para a coluna certa sem recarregar a
    // página inteira (e perder filtros e colunas expandidas).
    atualizar = async () => {
        this.setState({ atualizando: true });

        const colunas = this.state.colunas;
        await Promise.all(
            this.todasAsColunas().map(c => this.carregarColuna(c.id, colunas[c.id]?.pagina || 1, true))
        );

        this.setState({ atualizando: false, atualizadoEm: moment() });
    };

    abrirAtendimento = (atendimento) => {
        window.open(`/atendimentos/edit/${atendimento.uuid || atendimento.id}`, '_blank');
    };

    // Mesma regra da listagem: hoje = atenção, passado = atrasada.
    situacaoAcao = (atendimento) => {
        const acao = atendimento.proxima_acao;
        if (!acao || !acao.data_acao) return { tem: false, atrasada: false, texto: 'Sem próxima ação agendada' };

        const hoje = moment().tz("America/Sao_Paulo");
        const data = moment.tz(acao.data_acao, "America/Sao_Paulo").startOf("day");
        const hora = (acao.hora_acao || '').split('.')[0].slice(0, 5);
        const texto = `${formataDataExibicao(acao.data_acao)}${hora ? ` ${hora}` : ''}`;

        if (data.isBefore(hoje, "day")) return { tem: true, atrasada: true, texto: `Atrasada · ${texto}` };
        if (data.isSame(hoje, "day")) return { tem: true, atrasada: true, hoje: true, texto: `Hoje · ${texto}` };
        return { tem: true, atrasada: false, texto };
    };

    alternarRecolhida = (etapaId) => {
        this.setState(prev => ({ recolhidas: { ...prev.recolhidas, [etapaId]: !prev.recolhidas[etapaId] } }));
    };

    renderCard = (atendimento) => {
        const acao = this.situacaoAcao(atendimento);
        const status = atendimento.dados_programa_status;
        const cor = corDoStatus(status);
        const laboratorio = atendimento.dados_laboratorio?.nome;

        return (
            <div
                key={atendimento.id}
                onClick={() => this.abrirAtendimento(atendimento)}
                title="Abrir atendimento em nova aba"
                style={{
                    //o card so se destaca porque o corpo da coluna e cinza; a elevacao
                    //reforca a separacao sem precisar de borda mais escura
                    background: '#fff', border: '1px solid #e2e8f1', borderLeft: `3px solid ${cor}`,
                    borderRadius: '6px', padding: '9px 10px', cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(37, 47, 74, .06), 0 3px 8px rgba(37, 47, 74, .05)',
                }}
            >
                <div className="d-flex align-items-baseline gap-2">
                    <span style={{ flex: 1, fontSize: '13px', fontWeight: 600, lineHeight: 1.25, color: '#16233a', overflowWrap: 'anywhere' }}>
                        {atendimento.dados_beneficiario?.nome || 'Sem paciente'}
                    </span>
                    <span style={{ fontSize: '11px', color: '#98a3b3', whiteSpace: 'nowrap' }}>#{atendimento.id}</span>
                </div>

                <div className="d-flex align-items-center gap-1" style={{
                    marginTop: '7px', fontSize: '11.5px',
                    color: acao.atrasada ? '#a41c12' : (acao.tem ? '#4a5768' : '#a3adbd'),
                    fontWeight: acao.atrasada ? 600 : 400,
                }}>
                    <span>{acao.atrasada ? '▲' : (acao.tem ? '◷' : '·')}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acao.texto}</span>
                </div>

                {(status?.nome || laboratorio) && (
                    <div className="d-flex align-items-center gap-2 flex-wrap" style={{ marginTop: '8px', paddingTop: '7px', borderTop: '1px dashed #e8edf4' }}>
                        {status?.nome && (
                            <span
                                title={`Status do atendimento: ${status.nome}`}
                                style={{
                                    display: 'inline-block', fontSize: '10.5px', fontWeight: 600, padding: '2px 7px', borderRadius: '3px',
                                    color: cor === COR_NEUTRA ? '#475569' : '#fff',
                                    background: cor === COR_NEUTRA ? '#eef2f7' : cor,
                                }}
                            >
                                {status.nome}
                            </span>
                        )}
                        {laboratorio && (
                            <span style={{ fontSize: '10.5px', color: '#98a3b3' }}>{laboratorio}</span>
                        )}
                    </div>
                )}
            </div>
        );
    };

    renderColuna = (etapa, indice, totalGeral, especial = false) => {
        const coluna = this.state.colunas[etapa.id] || { itens: [], total: 0, carregando: true };
        const { itens, total, carregando } = coluna;
        const vazia = !carregando && total === 0;
        const recolhida = this.state.recolhidas[etapa.id] || (this.state.recolherVazias && vazia);
        // colunas de desfecho não fazem parte da sequência da jornada: sem número
        const rotuloIndice = especial ? '' : String(indice + 1).padStart(2, '0');

        // "Sem etapa" só existe quando há alguém nela
        if (especial && etapa.ocultarSeVazia && !carregando && total === 0) return null;

        if (recolhida) {
            return (
                <div key={etapa.id} style={{ flex: '0 0 auto', width: `${LARGURA_COLUNA_RECOLHIDA}px`, alignSelf: 'stretch' }}>
                    <div
                        onClick={() => this.alternarRecolhida(etapa.id)}
                        title={`${etapa.nome} — ${total} atendimento(s). Clique para expandir.`}
                        style={{
                            height: '100%', minHeight: '420px', display: 'flex', flexDirection: 'column',
                            alignItems: 'center', gap: '10px', padding: '10px 0', cursor: 'pointer',
                            background: '#fff', border: '1px solid #dde3ec', borderRadius: '8px',
                        }}
                    >
                        <span style={{ fontSize: '11px', color: '#8b97a8' }}>{rotuloIndice}</span>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: total === 0 ? '#a3adbd' : '#3d4b5e' }}>{total}</span>
                        <span style={{ writingMode: 'vertical-rl', fontSize: '12px', fontWeight: 600, color: '#46536a', flex: 1, overflow: 'hidden' }}>
                            {etapa.nome}
                        </span>
                    </div>
                </div>
            );
        }

        const fatia = totalGeral ? Math.round((total / totalGeral) * 100) : 0;

        return (
            <div key={etapa.id} style={{ flex: '0 0 auto', width: `${LARGURA_COLUNA}px`, alignSelf: 'stretch' }}>
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff', border: '1px solid #dde3ec', borderRadius: '8px', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 11px 8px', borderBottom: '1px solid #eef1f6' }}>
                        <div className="d-flex align-items-start gap-2">
                            <span style={{ fontSize: '11px', color: '#a3adbd', paddingTop: '1px' }}>{rotuloIndice}</span>
                            <span style={{ flex: 1, fontSize: '12.5px', fontWeight: 600, lineHeight: 1.3, color: '#1b2537' }}>{etapa.nome}</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#3d4b5e', background: '#eef2f8', borderRadius: '10px', padding: '1px 8px' }}>
                                {carregando && !itens.length ? '…' : total}
                            </span>
                            <button
                                onClick={() => this.alternarRecolhida(etapa.id)}
                                title="Recolher etapa"
                                style={{ border: 0, background: 'transparent', color: '#9aa5b5', fontSize: '11px', cursor: 'pointer', padding: '2px' }}
                            >
                                ◂
                            </button>
                        </div>

                        {/* O resumo de "sem atrasos / N parados há X dias" fica junto com o
                            tempo na etapa, desligado até existir prazo esperado por etapa. */}

                        <div style={{ marginTop: '8px', height: '3px', borderRadius: '3px', background: '#eef2f8', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.max(fatia, total ? 3 : 0)}%`, background: fatia > 20 ? '#0f2748' : '#8ba4c9' }}></div>
                        </div>
                    </div>

                    {/* Corpo da coluna em cinza firme: é ele que faz os cards brancos
                        aparecerem. Com #f8fafc ficava branco sobre branco. */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '9px', display: 'flex', flexDirection: 'column', gap: '8px', background: '#eef2f7', minHeight: '120px', maxHeight: '520px' }}>
                        {carregando && !itens.length ? (
                            <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
                        ) : itens.length === 0 ? (
                            <div style={{ border: '1px dashed #d5dde9', borderRadius: '6px', padding: '18px 10px', textAlign: 'center', fontSize: '11.5px', color: '#9aa5b5', background: '#fff' }}>
                                Nenhum atendimento nesta etapa
                            </div>
                        ) : itens.map(this.renderCard)}
                    </div>

                    {itens.length < total && (
                        <div className="d-flex align-items-center gap-2" style={{ borderTop: '1px solid #eef1f6', background: '#fff', padding: '8px 10px' }}>
                            <span style={{ fontSize: '11px', color: '#7b8798' }}>{itens.length} de {total}</span>
                            <div style={{ flex: 1 }}></div>
                            <Button
                                size="sm"
                                variant="outline-success"
                                disabled={carregando}
                                onClick={() => this.carregarColuna(etapa.id, (coluna.pagina || 1) + 1)}
                                style={{ fontSize: '11.5px', padding: '4px 10px' }}
                            >
                                {carregando ? 'Carregando…' : `Ver mais ${Math.min(LIMITE_COLUNA, total - itens.length)}`}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    render() {
        const { etapas, carregandoEtapas, erro, colunas, recolherVazias, atualizando, atualizadoEm } = this.state;

        if (carregandoEtapas) return <div className="text-center py-5"><Spinner animation="border" /></div>;
        if (erro) return <div className="py-4" style={{ color: '#a41c12' }}>{erro}</div>;
        if (!etapas.length) return <div className="py-4" style={{ color: '#6b7a8d' }}>Este programa não tem etapas cadastradas.</div>;

        const especiais = this.colunasEspeciais().filter(this.colunaEspecialCabe);

        // o "N atendimentos" do topo conta o fluxo (etapas + sem etapa); os desfechos
        // têm coluna própria e entram como contagem à parte
        const totalFluxo = [...etapas, ...especiais.filter(e => e.id === 'sem_etapa')]
            .reduce((soma, c) => soma + (colunas[c.id]?.total || 0), 0);
        const totalGeral = totalFluxo;

        return (
            <div>
                <div className="d-flex align-items-center gap-3 flex-wrap mb-2" style={{ fontSize: '12px', color: '#6b7a8d' }}>
                    <Form.Check
                        type="checkbox"
                        id="kanbanRecolherVazias"
                        label="Recolher etapas vazias"
                        checked={recolherVazias}
                        onChange={() => this.setState({ recolherVazias: !recolherVazias })}
                        style={{ fontSize: '12px', color: '#4a5768' }}
                    />
                    <span>
                        {totalGeral} {totalGeral === 1 ? 'atendimento' : 'atendimentos'} em andamento
                        {especiais.some(e => e.id === 'finalizados' || e.id === 'cancelados') && ' · finalizados e cancelados nas colunas do fim'}
                    </span>
                    <div style={{ flex: 1 }}></div>
                    <span className="d-flex align-items-center gap-1">
                        <span style={{ color: '#a41c12', fontWeight: 700 }}>▲</span> ação atrasada
                    </span>
                    {atualizadoEm && (
                        <span style={{ color: '#8a97a8' }}>atualizado às {atualizadoEm.format('HH:mm')}</span>
                    )}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={this.atualizar}
                        disabled={atualizando}
                        title="Atualizar os cards sem recarregar a página"
                        style={{ fontSize: '11px', padding: '3px 10px', lineHeight: '16px' }}
                    >
                        {atualizando
                            ? <Spinner animation="border" size="sm" style={{ width: '11px', height: '11px', borderWidth: '1.5px' }} />
                            : <FaSyncAlt size={11} />
                        }
                        <span className="ms-1">Atualizar</span>
                    </Button>
                </div>

                {/* sem fundo próprio: o cinza da aplicação separa as colunas brancas */}
                <div className="d-flex gap-2 align-items-stretch" style={{ overflowX: 'auto', padding: '4px 0 12px' }}>
                    {etapas.map((etapa, i) => this.renderColuna(etapa, i, totalGeral))}
                    {especiais.map(coluna => this.renderColuna(coluna, 0, totalGeral, true))}
                </div>
            </div>
        );
    }
}

export default KanbanAtendimentos;
